import type { EncryptedBlob } from '@shared/types';
import { bytesToBase64, bytesToUtf8, base64ToBytes, toArrayBuffer, utf8ToBytes } from '../utils/encoding';

export type MessageArchivePlainManifest = {
  version: 1;
  archiveId?: string;
  sourceCircleIdentityId: string;
  destinationType: 'circle';
  destinationConfig: { circle: 'current' };
  mode: 'text';
  writerDeviceId: string;
  segmentation: {
    type: 'calendar';
    unit: 'day' | 'week' | 'month';
    timezone: 'UTC';
    finalizeGraceDays: number;
  };
  currentPeriodKey: string;
  periods: Record<string, {
    state: 'current' | 'final';
    revision: number;
    periodStartedAt: string | null;
    periodEndedAt: string;
    messageCount?: number;
    byteSize?: number;
  }>;
  updatedAt: string;
};

export type MessageArchivePlainSegment = {
  version: 1;
  periodKey: string;
  periodStartedAt: string | null;
  periodEndedAt: string;
  state: 'current' | 'final';
  createdAt: string;
  conversationSnapshots?: Record<string, {
    conversationId: string;
    type: 'direct' | 'group';
    title: string;
    directThreadKey?: string;
    groupChatId?: string;
    participants: Array<{
      identityId: string;
      displayName?: string | null;
      role?: 'self' | 'peer' | 'participant';
    }>;
    capturedAt: string;
  }>;
  messages: Array<Record<string, unknown>>;
};

export async function generateMessageArchiveKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function exportMessageArchiveKeyRaw(key: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', key);
  return bytesToBase64(new Uint8Array(raw));
}

export async function importMessageArchiveKeyRaw(rawBase64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    toArrayBuffer(base64ToBytes(rawBase64)),
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function encryptMessageArchiveJson(key: CryptoKey, value: unknown): Promise<EncryptedBlob> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = utf8ToBytes(JSON.stringify(value));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(nonce) },
    key,
    toArrayBuffer(plaintext)
  );
  return {
    cipher: 'aes-256-gcm',
    data: bytesToBase64(new Uint8Array(encrypted)),
    nonce: bytesToBase64(nonce),
    version: 1
  };
}

export async function decryptMessageArchiveJson<T>(key: CryptoKey, blob: EncryptedBlob): Promise<T> {
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(base64ToBytes(blob.nonce)) },
    key,
    toArrayBuffer(base64ToBytes(blob.data))
  );
  return JSON.parse(bytesToUtf8(new Uint8Array(decrypted))) as T;
}
